import React, { useState, useEffect } from 'react';
import { Modal, Button, Typography, Radio, Popover } from 'antd';
import { ExclamationCircleOutlined, CloudOutlined, FileOutlined } from '@ant-design/icons';
import { boardServices } from '../../api';
import { formatFileSize, removeOfflineData } from '../../utils/helper';
import { useDocument } from '../../context/documentInfoContex';
import { useCanvas } from '../../context/canvasContext';
import { useParams } from 'react-router-dom';
import './style.css';
import PreviewVersionModal from './previewVersionModal';
import { emitCanvasData, emitUpdateAll } from '../../socket';

const { Title, Text } = Typography;

const DocumentDataConflictModal = ({ isConflictModalVisible, setIsConflictModalVisible }) => {
    const { id } = useParams();
    const { setSavingData, setBoardData, serverData, isLoadingVersion, conflictPreviewVerion } = useDocument();
    const { canvasRef, saveStateToReloadStack } = useCanvas();
    const [localData, setLocalData] = useState(null);
    const [serverCanvasData, setServerCanvasData] = useState(null);
    const [selectedRadioOption, setSelectedRadioOption] = useState(null);
    const [previewVisible, setPreviewVisible] = useState(false);

    useEffect(() => {
        if (!isConflictModalVisible) return;

        const fetchData = async () => {
            try {
                const offlineData = JSON.parse(localStorage.getItem('offlineBoardData') || '{}')[id];
                const serverCanvasData = serverData?.mileboardData?.[0];
                setLocalData(offlineData);
                setServerCanvasData(serverCanvasData);
            } catch (error) {
                console.error('Error fetching conflict data:', error);
                setIsConflictModalVisible(false);
            }
        };
        fetchData();
    }, [id, isConflictModalVisible, serverData]);

    const loadCanvasFromData = (data, isKeepOffline = false) => {

        if (!data || !canvasRef.current) {
            console.error('Invalid data or canvasRef:', { data, canvasRef });
            return;
        }

        const fileSize = formatFileSize(new TextEncoder().encode(JSON.stringify(data)).length);
        setSavingData({ isSaving: '', lastSavedDateTime: '', fileSize });
        isLoadingVersion.current = true;

        canvasRef.current.loadFromJSON(data, () => {
            canvasRef.current.renderAll();
            const updatedBoardData = canvasRef.current.toJSON(['selectable', 'id', 'type']);
            setBoardData(updatedBoardData);
            saveStateToReloadStack();
            isKeepOffline && emitUpdateAll({ roomID: id, data: { message: "offline version loaded" } });
            isLoadingVersion.current = false;
        });
        removeOfflineData(id);
    };

    const handleKeepOffline = async () => {
        if (!localData) {
            console.error('Local data is not available.');
            onClose();
            return;
        }

        try {
            loadCanvasFromData(localData?.data, true);
            await boardServices.updateBoardData(id, localData?.data, setSavingData);
        } catch (error) {
            console.error('Failed to keep offline data:', error);
        } finally {
            onClose();
        }
    };

    const handleUseServer = () => {
        if (!serverCanvasData) {
            console.error('No server data available');
            onClose();
            return;
        }

        try {
            loadCanvasFromData(serverCanvasData);
        } catch (error) {
            console.error('Failed to load server data:', error);
        } finally {
            onClose();
        }
    };

    const onClose = () => {
        setIsConflictModalVisible(false);
    };

    const handleVersionClick = (version) => {
        setPreviewVisible(true);
        setSelectedRadioOption(version);
    };

    if (conflictPreviewVerion) return null;

    return (
        <div>
            <Modal
                visible={isConflictModalVisible}
                onCancel={() => { }}
                closable={false}
                maskClosable={false}
                footer={null}
                width={480}
                centered
                className="document-conflict-modal"
                closeIcon={<span className="close-icon">×</span>}
            >
                <div className="modal-content">
                    <div className="warning-icon-container">
                        <ExclamationCircleOutlined className="warning-icon" />
                    </div>
                    <Title level={3} className="modal-title">
                        Document Version Conflict
                    </Title>
                    <Text className="modal-description">
                        A newer version of this document is available on the server. Would you like to retain your offline changes or use the server version?
                    </Text>
                    <div className="version-options">
                        <Radio.Group
                            value={selectedRadioOption}
                            // onChange={handleRadioChange}
                            className="version-radio-group"
                        >
                            <div className={`version-option ${selectedRadioOption === 'offline' ? 'selected' : ''}`}>
                                <div>
                                    <Radio value="offline" className="version-radio" onClick={() => handleVersionClick('offline')}>
                                        <div className="version-content">
                                            <FileOutlined className="version-icon offline-icon" />
                                            <div className="version-text">
                                                <div className="version-title">Offline Version</div>
                                                <div className="version-subtitle">Your local changes</div>
                                            </div>
                                        </div>
                                    </Radio>
                                </div>
                            </div>

                            <div className={`version-option ${selectedRadioOption === 'server' ? 'selected' : ''}`}>
                                <div>
                                    <Radio value="server" className="version-radio" onClick={() => handleVersionClick('server')}>
                                        <div className="version-content">
                                            <CloudOutlined className="version-icon server-icon" />
                                            <div className="version-text">
                                                <div className="version-title">Server Version</div>
                                                <div className="version-subtitle">Latest from server</div>
                                            </div>
                                        </div>
                                    </Radio>
                                </div>
                            </div>
                        </Radio.Group>
                    </div>
                    <div className="modal-actions">
                        <Button type="primary" size="large" className="keep-offline-btn" onClick={handleKeepOffline}>
                            <FileOutlined /> Keep Offline Changes
                        </Button>
                        <Button size="large" className="use-server-btn" onClick={handleUseServer}>
                            <CloudOutlined /> Use Server Version
                        </Button>
                    </div>
                </div>
            </Modal>
            {previewVisible && selectedRadioOption && (
                <PreviewVersionModal
                    version={selectedRadioOption}
                    isvisible={previewVisible}
                    setIsVisible={setPreviewVisible}
                />
            )}
        </div>
    );
};

export default DocumentDataConflictModal;